<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-header">
        <h1>登录</h1>
        <router-link to="/" class="home-link">
          <span class="home-icon">⌂</span>
          返回主页
        </router-link>
      </div>
      
      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label for="username">用户名</label>
          <div class="input-wrapper">
            <span class="input-icon">👤</span>
            <input 
              type="text" 
              v-model="username" 
              class="form-control" 
              id="username" 
              placeholder="请输入用户名"
              required 
            />
          </div>
        </div>
        
        <div class="form-group">
          <label for="password">密码</label>
          <div class="input-wrapper">
            <span class="input-icon">🔒</span>
            <input 
              type="password" 
              v-model="password" 
              class="form-control" 
              id="password"  
              placeholder="请输入密码"
              autocomplete="on" 
              required 
            />
          </div>
        </div>

        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <button 
          type="submit" 
          class="btn-primary" 
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="loading-spinner"></span>
          <span v-else>登录</span>
        </button>
      </form>
      <button class="btn-secondary" @click="showRegister = true" style="width:100%;margin-top:12px;">注册</button>
      <div v-if="showRegister" class="register-dialog">
        <div class="register-content">
          <h2>用户注册</h2>
          <form @submit.prevent="handleRegister">
            <div class="form-group">
              <label for="register-username">用户名</label>
              <input id="register-username" v-model="registerUsername" class="form-control" required placeholder="请输入用户名" />
            </div>
            <div class="form-group">
              <label for="register-password">密码</label>
              <input id="register-password" type="password" v-model="registerPassword" class="form-control" required placeholder="请输入密码" />
            </div>
            <div class="form-group">
              <label for="register-email">邮箱</label>
              <input id="register-email" type="email" v-model="registerEmail" class="form-control" required placeholder="请输入邮箱" />
            </div>
            <div style="display:flex;gap:12px;margin-top:16px;">
              <button type="submit" class="btn-primary" :disabled="isLoading">注册</button>
              <button type="button" class="btn-secondary" @click="showRegister = false">取消</button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <!-- flash 提示条 -->
    <div v-if="flashMessage" :class="['flash-bar', flashType]">
      {{ flashMessage }}
    </div>
    <div v-if="showPwdDialog" class="reset-pwd-dialog">
      <div class="reset-pwd-content">
        <h3>重置密码：{{ pwdUser?.userName }}</h3>
        <input v-model="newPassword" type="password" placeholder="新密码" />
        <div style="margin-top:12px;">
          <button @click="doResetPwd" :disabled="!newPassword">确定</button>
          <button @click="closePwdDialog">取消</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { login, resetUserPassword } from '@/services/apiService';
import { useAuthStore } from '@/stores/userStore';

export default {
  name: 'LoginPage',
  data() {
    return {
      username: '',
      password: '',
      isLoading: false,
      errorMessage: '',
      redirectPath: '/',
      fromPage: '',
      showRegister: false,
      registerUsername: '',
      registerPassword: '',
      registerEmail: '',
      // flash相关
      flashMessage: '',
      flashType: '',
      showPwdDialog: false,
      pwdUser: null,
      newPassword: ''
    };
  },
  created() {
    // 获取重定向路径和来源页面
    const { redirect, from } = this.$route.query;
    if (redirect) {
      this.redirectPath = redirect;
    }
    if (from) {
      this.fromPage = from;
    }
  },
  methods: {
    async handleLogin() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        const response = await login(this.username, this.password, this.setLoading);
        if (response && response.status === 0 && response.token) {
          // 使用 auth store 来更新状态
          const authStore = useAuthStore();
          authStore.setlogin(response.token, this.username);
          
          // 根据来源页面决定跳转行为
          if (this.fromPage) {
            // 如果有来源页面，返回到来源页面
            this.$router.push(this.redirectPath);
          } else if (this.$route.query.redirect) {
            // 如果有重定向参数，跳转到指定页面
            this.$router.push(this.redirectPath);
          } else {
            // 默认跳转到管理面板
            this.$router.push('/admin');
          }
        } else {
          this.errorMessage = response.errorMsg || '登录失败，请检查用户名和密码';
        }
      } catch (error) {
        console.error('Login error:', error);
        this.errorMessage = '登录失败，请稍后重试';
      } finally {
        this.isLoading = false;
      }
    },
    setLoading(value) {
      this.isLoading = value;
    },
    showFlash(msg, type = 'success') {
      this.flashMessage = msg;
      this.flashType = type;
      setTimeout(() => {
        this.flashMessage = '';
      }, 2000);
    },
    async handleRegister() {
      // 1. 必填校验
      if (!this.registerUsername || !this.registerPassword || !this.registerEmail) {
        this.showFlash('用户名、密码和邮箱不能为空！', 'error');
        return;
      }
      
      // 2. 密码长度校验
      if (this.registerPassword.length < 6) {
        this.showFlash('密码长度不能少于6位！', 'error');
        return;
      }

      // 3. 密码复杂度校验（数字和字母）
      const hasNumber = /\d/.test(this.registerPassword);
      const hasLetter = /[a-zA-Z]/.test(this.registerPassword);
      if (!hasNumber || !hasLetter) {
        this.showFlash('密码必须包含数字和字母！', 'error');
        return;
      }

      // 4. 邮箱格式校验
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.registerEmail)) {
        this.showFlash('请输入有效的邮箱地址！', 'error');
        return;
      }

      this.isLoading = true;
      try {
        const { register } = await import('@/services/apiService');
        const response = await register(this.registerUsername, this.registerPassword, this.registerEmail, this.setLoading);
        if (response && response.status === 0) {
          this.showFlash('注册成功，待激活后登录！', 'success');
          this.showRegister = false;
        } else {
          this.showFlash(response?.errorMsg || '注册失败', 'error');
        }
      } catch (error) {
        this.showFlash(error.message || '注册失败，请稍后重试', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    openResetPwdDialog(user) {
      this.pwdUser = user;
      this.newPassword = '';
      this.showPwdDialog = true;
    },
    closePwdDialog() {
      this.showPwdDialog = false;
      this.pwdUser = null;
      this.newPassword = '';
    },
    async doResetPwd() {
      if (!this.pwdUser || !this.newPassword) return;
      this.loading = true;
      try {
        const authStore = useAuthStore();
        const token = authStore.token;
        await resetUserPassword(this.pwdUser.id, this.newPassword, token, this.pwdUser.userName);
        this.showFlash('密码已重置', 'success');
        this.closePwdDialog();
      } catch (err) {
        this.showFlash(err.statusText || err.message || '重置失败', 'error');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-bg);
  padding: 20px;
}

.login-container {
  width: 100%;
  max-width: 400px;
  padding: 32px;
  background-color: var(--secondary-bg);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.login-header h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
}

.home-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.875rem;
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  transition: all 0.2s ease;
}

.home-link:hover {
  background-color: var(--hover-bg);
  border-color: var(--accent-color);
}

.home-icon {
  font-size: 1.2em;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: var(--text-secondary);
  font-size: 1.2em;
}

.form-control {
  width: 100%;
  padding: 10px 12px 10px 40px;
  font-size: 0.875rem;
  line-height: 1.6;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background-color: var(--primary-bg);
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.form-control:focus {
  border-color: var(--accent-color);
  outline: none;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

.form-control::placeholder {
  color: var(--text-secondary);
}

.error-message {
  padding: 12px;
  background-color: #ffebee;
  color: #c62828;
  border-radius: 6px;
  font-size: 0.875rem;
  text-align: center;
  border: 1px solid #ef9a9a;
}

.btn-primary {
  width: 100%;
  padding: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: var(--accent-color);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--text-primary);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.register-dialog {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.register-content {
  background: var(--secondary-bg);
  padding: 32px 24px;
  border-radius: 10px;
  min-width: 320px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.15);
}

.btn-secondary {
  background: var(--border-color);
  color: var(--text-primary);
  border: none;
  border-radius: 6px;
  padding: 10px 0;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover:not(:disabled) {
  background: var(--hover-bg);
}

.flash-bar {
  position: fixed;
  top: 32px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 200px;
  max-width: 90vw;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  color: #fff;
  z-index: 2000;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  animation: fadeInOut 2s;
}
.flash-bar.success {
  background: #10b981;
}
.flash-bar.error {
  background: #ef4444;
}
@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, 0.5rem); }
  10% { opacity: 1; transform: translate(-50%, 0); }
  90% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -0.5rem); }
}

.reset-pwd-dialog {
  position: fixed; top:0; left:0; right:0; bottom:0;
  background: rgba(0,0,0,0.2); display: flex; align-items: center; justify-content: center; z-index: 2001;
}
.reset-pwd-content {
  background: #fff; color: #222; padding: 32px 24px; border-radius: 10px; min-width: 320px; box-shadow: 0 4px 24px rgba(0,0,0,0.15);
}
.reset-pwd-content input { width: 100%; padding: 8px; margin-top: 8px; }
.reset-pwd-content button { margin-right: 12px; }
</style>